import { useState } from "react";
import { Building2, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";

// Sample invoice data
const sampleInvoice = {
  invoice_number: "INV-00042",
  invoice_date: "2026-01-15",
  due_date: "2026-02-15",
  owner: { name: "Sunrise Properties LLC", address: "100 Business Park Dr, Suite 200, Houston, TX 77001" },
  tenant: { name: "John Martinez", email: "john.martinez@email.com", phone: "(555) 234-5678" },
  property: { address: "789 Sunset Blvd, Unit 4B, Houston, TX 77002" },
  items: [
    { description: "Monthly Rent - February 2026", quantity: 1, unit_price: 1850.00, amount: 1850.00 },
    { description: "Parking Space #42", quantity: 1, unit_price: 75.00, amount: 75.00 },
    { description: "Pet Fee", quantity: 1, unit_price: 50.00, amount: 50.00 },
  ],
  subtotal: 1975.00,
  tax: 0,
  total: 1975.00,
  notes: "Please make payment via Zelle to payments@sunriseproperties.com or mail check to the address above."
};

// Style 1: Classic Professional (QuickBooks-inspired)
const InvoiceStyle1 = ({ invoice }) => (
  <div className="bg-white p-8 max-w-2xl mx-auto border border-slate-200 rounded-lg shadow-sm">
    {/* Header */}
    <div className="flex justify-between items-start border-b-2 border-slate-800 pb-6 mb-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">INVOICE</h1>
        <p className="text-slate-500 mt-1">#{invoice.invoice_number}</p>
      </div>
      <div className="text-right">
        <h2 className="text-xl font-bold text-slate-800">{invoice.owner.name}</h2>
        <p className="text-sm text-slate-500 mt-1 max-w-[200px]">{invoice.owner.address}</p>
      </div>
    </div>

    {/* Bill To & Dates */}
    <div className="grid grid-cols-2 gap-8 mb-8">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Bill To</p>
        <p className="font-semibold text-slate-800">{invoice.tenant.name}</p>
        <p className="text-sm text-slate-600">{invoice.property.address}</p>
        <p className="text-sm text-slate-500">{invoice.tenant.email}</p>
      </div>
      <div className="text-right">
        <div className="inline-block text-left">
          <div className="flex justify-between gap-8 text-sm mb-1">
            <span className="text-slate-500">Invoice Date:</span>
            <span className="font-medium">{invoice.invoice_date}</span>
          </div>
          <div className="flex justify-between gap-8 text-sm mb-1">
            <span className="text-slate-500">Due Date:</span>
            <span className="font-medium text-red-600">{invoice.due_date}</span>
          </div>
          <div className="flex justify-between gap-8 text-sm mt-3 pt-3 border-t">
            <span className="text-slate-500">Amount Due:</span>
            <span className="font-bold text-lg text-slate-800">${invoice.total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>

    {/* Items Table */}
    <table className="w-full mb-6">
      <thead>
        <tr className="border-b-2 border-slate-200">
          <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Description</th>
          <th className="text-center py-3 text-xs font-bold uppercase tracking-wider text-slate-500 w-20">Qty</th>
          <th className="text-right py-3 text-xs font-bold uppercase tracking-wider text-slate-500 w-28">Rate</th>
          <th className="text-right py-3 text-xs font-bold uppercase tracking-wider text-slate-500 w-28">Amount</th>
        </tr>
      </thead>
      <tbody>
        {invoice.items.map((item, i) => (
          <tr key={i} className="border-b border-slate-100">
            <td className="py-4 text-slate-700">{item.description}</td>
            <td className="py-4 text-center text-slate-600">{item.quantity}</td>
            <td className="py-4 text-right text-slate-600 tabular-nums">${item.unit_price.toFixed(2)}</td>
            <td className="py-4 text-right font-medium text-slate-800 tabular-nums">${item.amount.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>

    {/* Totals */}
    <div className="flex justify-end mb-8">
      <div className="w-64">
        <div className="flex justify-between py-2 text-sm">
          <span className="text-slate-500">Subtotal</span>
          <span className="tabular-nums">${invoice.subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between py-2 text-sm">
          <span className="text-slate-500">Tax</span>
          <span className="tabular-nums">${invoice.tax.toFixed(2)}</span>
        </div>
        <div className="flex justify-between py-3 border-t-2 border-slate-800 text-lg font-bold">
          <span>Total</span>
          <span className="tabular-nums">${invoice.total.toFixed(2)}</span>
        </div>
      </div>
    </div>

    {/* Notes */}
    <div className="bg-slate-50 p-4 rounded-lg">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Notes</p>
      <p className="text-sm text-slate-600">{invoice.notes}</p>
    </div>
  </div>
);

// Style 2: Modern Minimal with Color Accent
const InvoiceStyle2 = ({ invoice }) => (
  <div className="bg-white max-w-2xl mx-auto border border-slate-200 rounded-xl shadow-sm overflow-hidden">
    {/* Green Header */}
    <div className="bg-green-700 text-white p-8">
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="text-xl font-bold">{invoice.owner.name}</span>
          </div>
          <p className="text-green-100 text-sm">{invoice.owner.address}</p>
        </div>
        <div className="text-right">
          <p className="text-green-200 text-sm mb-1">Invoice</p>
          <p className="text-2xl font-bold">{invoice.invoice_number}</p>
        </div>
      </div>
    </div>

    <div className="p-8">
      {/* Bill To & Dates Row */}
      <div className="flex justify-between mb-8 pb-6 border-b border-slate-100">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-green-700 mb-2">Billed To</p>
          <p className="font-semibold text-slate-900 text-lg">{invoice.tenant.name}</p>
          <p className="text-slate-500 text-sm">{invoice.property.address}</p>
        </div>
        <div className="text-right">
          <div className="mb-3">
            <p className="text-xs text-slate-400">Issue Date</p>
            <p className="font-medium text-slate-700">{invoice.invoice_date}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Due Date</p>
            <p className="font-medium text-slate-700">{invoice.due_date}</p>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="mb-8">
        <div className="grid grid-cols-12 gap-4 text-xs font-semibold uppercase tracking-wider text-slate-400 pb-3 border-b border-slate-100">
          <div className="col-span-6">Item</div>
          <div className="col-span-2 text-center">Qty</div>
          <div className="col-span-2 text-right">Price</div>
          <div className="col-span-2 text-right">Total</div>
        </div>
        {invoice.items.map((item, i) => (
          <div key={i} className="grid grid-cols-12 gap-4 py-4 border-b border-slate-50">
            <div className="col-span-6 text-slate-700">{item.description}</div>
            <div className="col-span-2 text-center text-slate-500">{item.quantity}</div>
            <div className="col-span-2 text-right text-slate-500 tabular-nums">${item.unit_price.toFixed(2)}</div>
            <div className="col-span-2 text-right font-medium text-slate-800 tabular-nums">${item.amount.toFixed(2)}</div>
          </div>
        ))}
      </div>

      {/* Total Box */}
      <div className="flex justify-end">
        <div className="bg-green-50 rounded-xl p-6 w-72">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-500">Subtotal</span>
            <span className="text-slate-700 tabular-nums">${invoice.subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm mb-4">
            <span className="text-slate-500">Tax</span>
            <span className="text-slate-700 tabular-nums">${invoice.tax.toFixed(2)}</span>
          </div>
          <div className="flex justify-between pt-4 border-t border-green-200">
            <span className="font-semibold text-green-800">Total Due</span>
            <span className="text-2xl font-bold text-green-700 tabular-nums">${invoice.total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="mt-8 pt-6 border-t border-slate-100">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Payment Instructions</p>
        <p className="text-sm text-slate-500">{invoice.notes}</p>
      </div>
    </div>
  </div>
);

// Style 3: Bold & Contemporary
const InvoiceStyle3 = ({ invoice }) => (
  <div className="bg-slate-900 text-white max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-xl">
    {/* Header */}
    <div className="p-8 pb-12">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-slate-400 text-sm mb-1">From</p>
          <h2 className="text-xl font-bold">{invoice.owner.name}</h2>
        </div>
        <div className="text-right">
          <p className="text-6xl font-black text-slate-700">{invoice.invoice_number.split('-')[1]}</p>
          <p className="text-slate-400 text-sm mt-1">Invoice #{invoice.invoice_number}</p>
        </div>
      </div>
    </div>

    {/* White Content Area */}
    <div className="bg-white text-slate-900 rounded-t-3xl p-8">
      {/* Bill To */}
      <div className="flex justify-between mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Invoice To</p>
          <p className="text-xl font-bold text-slate-900">{invoice.tenant.name}</p>
          <p className="text-slate-500">{invoice.property.address}</p>
        </div>
        <div className="bg-slate-100 rounded-xl p-4 text-right">
          <p className="text-xs text-slate-400 mb-1">Due Date</p>
          <p className="text-lg font-bold text-slate-900">{invoice.due_date}</p>
        </div>
      </div>

      {/* Items */}
      <div className="bg-slate-50 rounded-xl p-6 mb-6">
        {invoice.items.map((item, i) => (
          <div key={i} className={`flex justify-between items-center py-4 ${i !== invoice.items.length - 1 ? 'border-b border-slate-200' : ''}`}>
            <div>
              <p className="font-medium text-slate-900">{item.description}</p>
              <p className="text-sm text-slate-400">{item.quantity} × ${item.unit_price.toFixed(2)}</p>
            </div>
            <p className="text-lg font-bold tabular-nums">${item.amount.toFixed(2)}</p>
          </div>
        ))}
      </div>

      {/* Total */}
      <div className="flex justify-between items-center p-6 bg-slate-900 text-white rounded-xl">
        <span className="text-slate-300">Total Amount</span>
        <span className="text-3xl font-black tabular-nums">${invoice.total.toFixed(2)}</span>
      </div>

      {/* Notes */}
      <div className="mt-6 text-center">
        <p className="text-sm text-slate-400">{invoice.notes}</p>
      </div>
    </div>
  </div>
);

// Style 4: Clean Corporate
const InvoiceStyle4 = ({ invoice }) => (
  <div className="bg-white max-w-2xl mx-auto border-2 border-slate-200 rounded-lg overflow-hidden">
    {/* Top Bar */}
    <div className="bg-blue-600 h-2"></div>
    
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
            SP
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">{invoice.owner.name}</h2>
            <p className="text-sm text-slate-500">Property Management</p>
          </div>
        </div>
        <div className="text-right">
          <h1 className="text-3xl font-light text-slate-300">INVOICE</h1>
          <p className="text-blue-600 font-mono font-bold">{invoice.invoice_number}</p>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-3 gap-6 mb-8 p-6 bg-slate-50 rounded-lg">
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Bill To</p>
          <p className="font-semibold text-slate-800">{invoice.tenant.name}</p>
          <p className="text-sm text-slate-500">{invoice.property.address}</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Invoice Date</p>
          <p className="font-medium text-slate-700">{invoice.invoice_date}</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Due Date</p>
          <p className="font-medium text-blue-600">{invoice.due_date}</p>
        </div>
      </div>

      {/* Items Table */}
      <table className="w-full mb-6">
        <thead>
          <tr className="bg-slate-800 text-white">
            <th className="text-left py-3 px-4 text-sm font-medium rounded-l-lg">Description</th>
            <th className="text-center py-3 px-4 text-sm font-medium w-20">Qty</th>
            <th className="text-right py-3 px-4 text-sm font-medium w-28">Rate</th>
            <th className="text-right py-3 px-4 text-sm font-medium w-28 rounded-r-lg">Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.items.map((item, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-slate-50' : ''}>
              <td className="py-4 px-4 text-slate-700">{item.description}</td>
              <td className="py-4 px-4 text-center text-slate-600">{item.quantity}</td>
              <td className="py-4 px-4 text-right text-slate-600 tabular-nums">${item.unit_price.toFixed(2)}</td>
              <td className="py-4 px-4 text-right font-semibold text-slate-800 tabular-nums">${item.amount.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end">
        <div className="w-72">
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Subtotal</span>
            <span className="tabular-nums">${invoice.subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Tax</span>
            <span className="tabular-nums">${invoice.tax.toFixed(2)}</span>
          </div>
          <div className="flex justify-between py-4 bg-blue-600 text-white px-4 rounded-lg mt-2">
            <span className="font-semibold">Total Due</span>
            <span className="text-xl font-bold tabular-nums">${invoice.total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-slate-200 text-center">
        <p className="text-sm text-slate-400">{invoice.notes}</p>
      </div>
    </div>
  </div>
);

const InvoicePreview = () => {
  const [selectedStyle, setSelectedStyle] = useState(null);

  const styles = [
    { id: 1, name: "Classic Professional", description: "QuickBooks-inspired, clean and traditional", component: InvoiceStyle1 },
    { id: 2, name: "Modern Green", description: "Color header accent, friendly and modern", component: InvoiceStyle2 },
    { id: 3, name: "Bold Contemporary", description: "Dark header, bold typography, eye-catching", component: InvoiceStyle3 },
    { id: 4, name: "Clean Corporate", description: "Blue accent, corporate feel, zebra stripes", component: InvoiceStyle4 },
  ];

  return (
    <div className="min-h-screen bg-slate-100 py-8" data-testid="invoice-preview-page">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Choose Your Invoice Style
          </h1>
          <p className="text-slate-500">Select the design that best fits your brand</p>
        </div>

        {/* Style Selector */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {styles.map((style) => (
            <button
              key={style.id}
              onClick={() => setSelectedStyle(style.id)}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                selectedStyle === style.id
                  ? 'border-green-600 bg-green-50'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid={`style-${style.id}-button`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-slate-900">Style {style.id}</span>
                {selectedStyle === style.id && (
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              <p className="font-medium text-slate-700">{style.name}</p>
              <p className="text-sm text-slate-500">{style.description}</p>
            </button>
          ))}
        </div>

        {/* Preview Area */}
        <div className="space-y-12">
          {styles.map((style) => (
            <div key={style.id} className="scroll-mt-8" id={`style-${style.id}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800">
                    Style {style.id}: {style.name}
                  </h2>
                  <p className="text-slate-500">{style.description}</p>
                </div>
                <Button
                  onClick={() => setSelectedStyle(style.id)}
                  variant={selectedStyle === style.id ? "default" : "outline"}
                  className={selectedStyle === style.id ? "bg-green-700" : ""}
                  data-testid={`select-style-${style.id}`}
                >
                  {selectedStyle === style.id ? "Selected" : "Select This Style"}
                </Button>
              </div>
              <style.component invoice={sampleInvoice} />
            </div>
          ))}
        </div>

        {/* Fixed Selection Bar */}
        {selectedStyle && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <p className="text-slate-600">
                Selected: <span className="font-semibold text-slate-900">{styles.find(s => s.id === selectedStyle)?.name}</span>
              </p>
              <Button className="bg-green-700 hover:bg-green-800" data-testid="confirm-style-button">
                Confirm Selection
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicePreview;
